/**
 * @file     useEmailConfirmedWatcher.ts
 * @purpose  A "check your email" screen should move on by itself once the link
 *           is clicked, often in another tab or app. While `active`, listens
 *           for the session (auth state change, a storage write from the other
 *           tab) and re-checks the server whenever this tab regains focus.
 *           Fires `onConfirmed` once per activation; the screens keep their
 *           Done button as the fallback.
 */
'use client';

import { useEffect, useRef } from 'react';
import { getSupabaseBrowserClient } from '@/core/supabase/client';

export function useEmailConfirmedWatcher(active: boolean, onConfirmed: () => void): void {
  const latest = useRef(onConfirmed);
  latest.current = onConfirmed;

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let fired = false;
    const fire = () => {
      if (stopped || fired) return;
      fired = true;
      latest.current();
    };

    const check = async () => {
      try {
        const supabase = await getSupabaseBrowserClient();
        if (!supabase) return;
        const { data } = await supabase.auth.getUser();
        if (data.user) fire();
      } catch {
        // The Done button stays as the fallback.
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    let unsubscribe: (() => void) | null = null;
    void (async () => {
      try {
        const supabase = await getSupabaseBrowserClient();
        if (!supabase || stopped) return;
        const { data } = supabase.auth.onAuthStateChange((_event: string, session: { user?: unknown } | null) => {
          if (session?.user) fire();
        });
        unsubscribe = () => data.subscription.unsubscribe();
      } catch {
        // Falls back to the focus and storage checks below.
      }
    })();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('storage', check);
    return () => {
      stopped = true;
      unsubscribe?.();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('storage', check);
    };
  }, [active]);
}
